import { Artboard } from '@/components/builder/artboard';
import { ThemeTokenSample, themeTokenAnchor, type ThemeTokenFamily } from '@/components/builder/token-navigation';
import { PageHeader } from '@/components/shell/page-header';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/cn';
import { useAppTheme, useAssets, useSetAppTheme, type Asset } from '@/lib/queries';
import { SEED_THEME } from '@/lib/seed-theme';
import { summarizeThemeToken } from '@/lib/theme-token-summary';
import { useContextStore } from '@/stores/context';
import { TEMPLATES, templateDoc } from '@/templates';
import { stepToExperienceDocument } from '@personalizehq/editor';
import { normalizeTheme } from '@personalizehq/schemas';
import type { ButtonVariant, Corners, FontToken, Gradient, Shadow, Theme, Viewport } from '@personalizehq/schemas/ir';
import { createFileRoute } from '@tanstack/react-router';
import { Blend, Box, ChevronRight, Layers3, Moon, MousePointerClick, Palette, Plus, Sun, Trash2, Type, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

// The app-default Theme editor — the wide sibling of the in-canvas Theme panel. It edits the ONE
// token dictionary every mobile experience inherits: colours (light/dark pairs), bundled fonts, and
// the paint-token families + button component of ADR 0014. Saving writes the app theme; a token is a
// key into a dictionary that ships in the same payload, so a device sees an edit only on the next
// REPUBLISH. Tokens are added and deleted, never renamed here: this page cannot see the other drafts
// that reference a key, so a rename would break them silently — delete makes the breakage visible.

type ThemeSearch = { family?: ThemeTokenFamily; token?: string };

const FAMILIES = [
  { id: 'colors', label: 'Colors', group: 'Foundations', icon: Palette, blurb: 'Light and dark values that adapt every experience to the device appearance.' },
  { id: 'fonts', label: 'Type', group: 'Foundations', icon: Type, blurb: 'Reusable type styles for consistent hierarchy, rhythm, and Dynamic Type behavior.' },
  { id: 'radii', label: 'Radius', group: 'Foundations', icon: Box, blurb: 'A shared corner language for surfaces, media, and controls.' },
  { id: 'elevations', label: 'Elevation', group: 'Foundations', icon: Layers3, blurb: 'Reusable shadows that create a consistent sense of depth.' },
  { id: 'gradients', label: 'Gradient', group: 'Foundations', icon: Blend, blurb: 'Color-token gradients that remain coherent in light and dark modes.' },
  { id: 'buttons', label: 'Buttons', group: 'Components', icon: MousePointerClick, blurb: 'Complete control styles composed from your foundation tokens.' },
] as const;

const FAMILY_SINGULAR: Record<ThemeTokenFamily, string> = {
  colors: 'color',
  fonts: 'font',
  radii: 'radius',
  elevations: 'elevation',
  gradients: 'gradient',
  buttons: 'button',
};

const isFamily = (value: unknown): value is ThemeTokenFamily => FAMILIES.some((f) => f.id === value);

export const Route = createFileRoute('/_authed/theme')({
  validateSearch: (search: Record<string, unknown>): ThemeSearch =>
    isFamily(search.family) && typeof search.token === 'string' ? { family: search.family, token: search.token } : {},
  component: ThemePage,
});

const PREVIEW_VIEWPORT: Viewport = { w: 320, h: 560, color_scheme: 'light', font_scale: 1, direction: 'ltr' };
const NONE = '__none__';
const CUSTOM = '__custom__';

function ThemePage(): React.JSX.Element {
  const { family, token } = Route.useSearch();
  const appId = useContextStore((s) => s.appId);
  const { data: theme, isLoading } = useAppTheme(appId);
  const { data: assets } = useAssets(appId);
  const save = useSetAppTheme(appId);

  const [draft, setDraft] = useState<Theme | null>(null);
  const [scheme, setScheme] = useState<'light' | 'dark'>('light');
  // The active tab follows the deep-link `family` search param until the author picks one manually —
  // a derived value, so a deep-link never has to setState inside an effect (cascading renders).
  const [tabOverride, setTabOverride] = useState<ThemeTokenFamily | null>(null);
  const tab = tabOverride ?? family ?? 'colors';

  // An app has NO theme until one is written — the API stores NULL and invents nothing. The seed is
  // the dashboard's; it becomes real the moment the author saves. `normalizeTheme` backfills the
  // paint-token families on any theme that predates them (ADR 0014) so every family renders.
  const current = useMemo(() => normalizeTheme(draft ?? theme ?? SEED_THEME), [draft, theme]);
  const unsaved = draft !== null || theme == null;
  const linkedTokenExists = family && token ? Object.prototype.hasOwnProperty.call(current[family], token) : true;

  useEffect(() => {
    if (isLoading || !family || !token || !linkedTokenExists) return;
    const raf = requestAnimationFrame(() => {
      const target = document.getElementById(themeTokenAnchor(family, token));
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(raf);
  }, [family, isLoading, linkedTokenExists, token]);

  if (isLoading) return <Skeleton className="h-96 w-full rounded-lg" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Theme"
        description="Shape the design language shared by every mobile experience."
        actions={
          <div className="flex items-center gap-3">
            {unsaved ? <span className="rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-400">Unsaved</span> : null}
            <Button
              disabled={!unsaved || save.isPending}
              onClick={() =>
                save.mutate(current, {
                  onSuccess: () => {
                    setDraft(null);
                    toast.success('Theme saved. Republish a campaign for devices to see it.');
                  },
                  onError: (error: Error) => toast.error(error.message),
                })
              }
            >
              {save.isPending ? 'Saving…' : 'Save theme'}
            </Button>
          </div>
        }
      />

      {theme == null ? (
        <Banner>This app has no theme yet. What you see is the starter set — save it to make it real.</Banner>
      ) : null}
      {family && token && !linkedTokenExists ? (
        <Banner role="status">
          The {FAMILY_SINGULAR[family]} token “{token}” no longer exists. It may have been deleted since this link was created.
        </Banner>
      ) : null}

      <div className="grid items-start gap-5 min-[1400px]:grid-cols-[168px_minmax(0,1fr)_320px]">
        <ThemeFamilyNav active={tab} theme={current} onChange={setTabOverride} />

        <div className="min-w-0">
          {FAMILIES.filter((f) => f.id === tab).map((f) => (
            <FamilySection
              key={f.id}
              family={f.id}
              label={f.label}
              blurb={f.blurb}
              theme={current}
              assets={assets ?? []}
              onChange={setDraft}
              linkedToken={family === f.id ? token : undefined}
            />
          ))}
        </div>

        {/* The preview is the PRODUCTION renderer over a real template — the same code path the device
            runs, re-laid-out on every token edit. */}
        <div className="overflow-hidden rounded-xl border bg-card min-[1400px]:sticky min-[1400px]:top-6 min-[1400px]:self-start">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div>
              <span className="text-sm font-semibold">Preview</span>
              <p className="text-[11px] text-muted-foreground">Production renderer</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Sun className="size-3.5" />
              <Switch aria-label="Toggle dark mode preview" checked={scheme === 'dark'} onCheckedChange={(on) => setScheme(on ? 'dark' : 'light')} />
              <Moon className="size-3.5" />
            </label>
          </div>
          <div className="flex justify-center bg-muted/40 p-4">
            <ThemePreview theme={current} scheme={scheme} assets={assets ?? []} />
          </div>
          <p className="border-t px-4 py-2.5 text-center text-[11px] text-muted-foreground">Starter modal · updates as you edit</p>
        </div>
      </div>
    </div>
  );
}

function ThemeFamilyNav({ active, theme, onChange }: { active: ThemeTokenFamily; theme: Theme; onChange: (family: ThemeTokenFamily) => void }): React.JSX.Element {
  return (
    <nav aria-label="Theme token families" className="rounded-xl border bg-card p-2 min-[1400px]:sticky min-[1400px]:top-6">
      {(['Foundations', 'Components'] as const).map((group) => (
        <div key={group} className={cn(group === 'Components' && 'mt-3 border-t pt-3')}>
          <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{group}</p>
          <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 min-[1400px]:!grid-cols-1">
            {FAMILIES.filter((family) => family.group === group).map((family) => {
              const Icon = family.icon;
              const selected = active === family.id;
              return (
                <button
                  key={family.id}
                  type="button"
                  aria-current={selected ? 'page' : undefined}
                  onClick={() => onChange(family.id)}
                  className={cn(
                    'group flex h-9 min-w-0 items-center gap-2 rounded-lg px-2 text-left text-xs transition-colors',
                    selected ? 'bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Icon className="size-3.5 shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{family.label}</span>
                  <span className={cn('text-[10px] tabular-nums', selected ? 'text-primary/70' : 'text-muted-foreground/70')}>{Object.keys(theme[family.id]).length}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function Banner({ children, role }: { children: React.ReactNode; role?: string }): React.JSX.Element {
  return (
    <p role={role} className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-700 dark:text-amber-400">
      {children}
    </p>
  );
}

function ThemePreview({ theme, scheme, assets }: { theme: Theme; scheme: 'light' | 'dark'; assets: Asset[] }): React.JSX.Element {
  const doc = useMemo(() => {
    const authoring = templateDoc(TEMPLATES[0]!, theme);
    return stepToExperienceDocument(authoring, authoring.steps[0]!.id);
  }, [theme]);
  const assetMap = useMemo(() => Object.fromEntries(assets.map((a) => [a.id, a.url])), [assets]);
  const viewport = useMemo(() => ({ ...PREVIEW_VIEWPORT, color_scheme: scheme }), [scheme]);
  return <Artboard doc={doc} viewport={viewport} selection={[]} selectionAnchor={null} onSelect={() => {}} assets={assetMap} />;
}

// ---------------------------------------------------------------------------- section

function defaultValue(family: ThemeTokenFamily, theme: Theme): Theme[ThemeTokenFamily][string] | null {
  const firstColor = Object.keys(theme.colors)[0];
  switch (family) {
    case 'colors':
      return { light: '#000000', dark: '#ffffff' };
    case 'fonts':
      return { family: 'Inter', size: 16, weight: 400, line_height: 1.5, max_scale: 1.6 };
    case 'radii':
      return 12;
    case 'elevations':
      return firstColor ? { y: 4, blur: 12, color: firstColor } : null;
    case 'gradients':
      return firstColor ? { kind: 'linear', angle: 180, stops: [firstColor, firstColor] } : null;
    case 'buttons':
      return firstColor ? { bg: firstColor, fg: firstColor, radius: 8, padding: { top: 12, end: 20, bottom: 12, start: 20 } } : null;
  }
}

function FamilySection({
  family,
  label,
  blurb,
  theme,
  assets,
  onChange,
  linkedToken,
}: {
  family: ThemeTokenFamily;
  label: string;
  blurb: string;
  theme: Theme;
  assets: Asset[];
  onChange: (theme: Theme) => void;
  linkedToken?: string | undefined;
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const entries = Object.entries(theme[family]);
  const initialSelection = linkedToken && Object.prototype.hasOwnProperty.call(theme[family], linkedToken) ? linkedToken : entries[0]?.[0] ?? null;
  const [selectedName, setSelectedName] = useState<string | null>(initialSelection);
  const selected = selectedName && Object.prototype.hasOwnProperty.call(theme[family], selectedName) ? selectedName : initialSelection;
  const selectedValue = selected ? (theme[family] as Record<string, unknown>)[selected] : undefined;
  const setFamily = (next: Record<string, unknown>): void => onChange({ ...theme, [family]: next });
  const needsColor = (family === 'elevations' || family === 'gradients' || family === 'buttons') && Object.keys(theme.colors).length === 0;

  const add = (): void => {
    const key = name.trim();
    if (!key) return;
    if (Object.prototype.hasOwnProperty.call(theme[family], key)) {
      toast.error(`A ${FAMILY_SINGULAR[family]} named “${key}” already exists.`);
      return;
    }
    const value = defaultValue(family, theme);
    if (value === null) return;
    setFamily({ ...theme[family], [key]: value });
    setSelectedName(key);
    setName('');
    setCreating(false);
  };

  const update = (key: string, value: unknown): void => setFamily({ ...theme[family], [key]: value });
  const remove = (key: string): void => {
    const next = { ...theme[family] } as Record<string, unknown>;
    delete next[key];
    setFamily(next);
    setSelectedName(Object.keys(next)[0] ?? null);
  };

  return (
    <section className="overflow-hidden rounded-xl border bg-card">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3.5">
        <div>
          <h2 className="text-base font-semibold">{label}</h2>
          <p className="mt-0.5 max-w-xl text-xs leading-5 text-muted-foreground">{blurb}</p>
        </div>
        {needsColor ? (
          <p className="rounded-md bg-muted px-2.5 py-1.5 text-xs text-muted-foreground">Add a color token first</p>
        ) : (
          <Button type="button" size="sm" onClick={() => setCreating((current) => !current)}>
            <Plus className="size-4" /> New {FAMILY_SINGULAR[family]}
          </Button>
        )}
      </div>

      {creating ? (
        <form
          className="flex items-center gap-2 border-b bg-muted/30 px-4 py-3"
          onSubmit={(event) => {
            event.preventDefault();
            add();
          }}
        >
          <Input
            autoFocus
            className="h-9 min-w-0 flex-1"
            placeholder={`Name this ${FAMILY_SINGULAR[family]} token`}
            aria-label={`New ${FAMILY_SINGULAR[family]} token name`}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Button type="submit" size="sm" disabled={name.trim() === ''}>Create</Button>
          <Button type="button" variant="ghost" size="icon" className="size-9" aria-label="Cancel new token" onClick={() => { setCreating(false); setName(''); }}>
            <X className="size-4" />
          </Button>
        </form>
      ) : null}

      {entries.length === 0 ? (
        <div className="m-4 rounded-lg border border-dashed bg-muted/20 p-10 text-center">
          <p className="text-sm font-medium">No {label.toLowerCase()} tokens yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Create one to start building this part of your theme.</p>
        </div>
      ) : selected && selectedValue !== undefined ? (
        <div className="grid min-h-[380px] lg:grid-cols-[220px_minmax(0,1fr)]">
          <div className="border-b bg-muted/15 p-2 lg:border-b-0 lg:border-r">
            <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-1">
              {entries.map(([key, value]) => (
                <button
                  key={key}
                  id={themeTokenAnchor(family, key)}
                  type="button"
                  aria-pressed={selected === key}
                  onClick={() => setSelectedName(key)}
                  className={cn(
                    'group flex min-w-0 items-center gap-2.5 rounded-lg px-2.5 py-2 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
                    selected === key ? 'bg-background shadow-sm ring-1 ring-border' : 'hover:bg-muted/70',
                    linkedToken === key && 'ring-2 ring-primary ring-offset-1 ring-offset-background'
                  )}
                >
                  <span className="grid size-8 shrink-0 place-items-center rounded-md border bg-background">
                    <ThemeTokenSample family={family} token={key} theme={theme} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-mono text-[11px] font-medium text-foreground">{key}</span>
                    <span className="block truncate text-[10px] text-muted-foreground">{summarizeThemeToken(family, value, theme)}</span>
                  </span>
                  <ChevronRight className={cn('size-3.5 shrink-0 text-muted-foreground/50', selected === key && 'text-primary')} />
                </button>
              ))}
            </div>
          </div>

          <TokenCard family={family} name={selected} theme={theme} onDelete={() => remove(selected)}>
            {family === 'colors' ? (
              <ColorEditor value={selectedValue as Theme['colors'][string]} onChange={(v) => update(selected, v)} name={selected} />
            ) : family === 'fonts' ? (
              <FontEditor value={selectedValue as FontToken} assets={assets} onChange={(v) => update(selected, v)} />
            ) : family === 'radii' ? (
              <RadiusEditor value={selectedValue as number | Corners} onChange={(v) => update(selected, v)} />
            ) : family === 'elevations' ? (
              <ElevationEditor value={selectedValue as Shadow} theme={theme} onChange={(v) => update(selected, v)} />
            ) : family === 'gradients' ? (
              <GradientEditor value={selectedValue as Gradient} theme={theme} onChange={(v) => update(selected, v)} />
            ) : (
              <ButtonEditor value={selectedValue as ButtonVariant} theme={theme} onChange={(v) => update(selected, v)} />
            )}
          </TokenCard>
        </div>
      ) : null}
    </section>
  );
}

function TokenCard({
  family,
  name,
  theme,
  onDelete,
  children,
}: {
  family: ThemeTokenFamily;
  name: string;
  theme: Theme;
  onDelete: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      className="min-w-0 p-4"
    >
      <div className="mb-5 flex items-center gap-3 border-b pb-4">
        <span className="grid size-10 shrink-0 place-items-center rounded-lg border bg-muted/30">
          <ThemeTokenSample family={family} token={name} theme={theme} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-sm font-semibold">{name}</span>
          <span className="block text-[11px] capitalize text-muted-foreground">{FAMILY_SINGULAR[family]} token</span>
        </span>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8 text-muted-foreground hover:text-destructive" aria-label={`Delete ${name}`}>
              <Trash2 className="size-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete “{name}”?</AlertDialogTitle>
              <AlertDialogDescription>
                Any experience that references this {FAMILY_SINGULAR[family]} token will flag it as missing until you repoint it. This cannot be undone from here.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={onDelete}>
                Delete token
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <div className="max-w-xl">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------- field primitives

function NumField({ label, value, onChange, min, max, step = 1 }: { label: string; value: number; onChange: (value: number) => void; min?: number; max?: number; step?: number }): React.JSX.Element {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] text-muted-foreground">{label}</Label>
      <Input
        type="number"
        aria-label={label}
        className="h-9"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => {
          const next = event.target.valueAsNumber;
          if (Number.isFinite(next)) onChange(next);
        }}
      />
    </div>
  );
}

function ColorTokenSelect({ label, value, theme, onChange, allowNone }: { label: string; value: string | undefined; theme: Theme; onChange: (value: string | undefined) => void; allowNone?: boolean }): React.JSX.Element {
  return (
    <Select value={value ?? NONE} onValueChange={(v) => onChange(v === NONE ? undefined : v)}>
      <SelectTrigger className="h-9" aria-label={label}>
        <SelectValue placeholder="Select a colour" />
      </SelectTrigger>
      <SelectContent>
        {allowNone ? <SelectItem value={NONE}>None</SelectItem> : null}
        {Object.keys(theme.colors).map((c) => (
          <SelectItem key={c} value={c}>
            <span className="flex items-center gap-2">
              <ThemeTokenSample family="colors" token={c} theme={theme} compact />
              <span className="font-mono text-xs">{c}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------- family editors

function ColorEditor({ value, onChange, name }: { value: Theme['colors'][string]; onChange: (value: Theme['colors'][string]) => void; name: string }): React.JSX.Element {
  return (
    <div className="flex flex-wrap gap-4">
      {(['light', 'dark'] as const).map((mode) => (
        <label key={mode} className="flex items-center gap-2 text-sm">
          <span className="w-9 capitalize text-muted-foreground">{mode}</span>
          <input
            type="color"
            aria-label={`${name} ${mode}`}
            className="size-9 cursor-pointer rounded-md border bg-transparent"
            value={value[mode]}
            onChange={(event) => onChange({ ...value, [mode]: event.target.value })}
          />
          <span className="font-mono text-xs text-muted-foreground">{value[mode]}</span>
        </label>
      ))}
    </div>
  );
}

function FontEditor({ value, assets, onChange }: { value: FontToken; assets: Asset[]; onChange: (value: FontToken) => void }): React.JSX.Element {
  const fontAssets = assets.filter((a) => a.content_type.includes('font') || /\.(ttf|otf|woff2?)$/i.test(a.r2_key));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Labeled label="Family">
          <Input className="h-9" aria-label="font family" value={value.family} onChange={(event) => onChange({ ...value, family: event.target.value })} />
        </Labeled>
        <Labeled label="Font file">
          <Select value={value.src ?? NONE} onValueChange={(v) => onChange({ ...value, src: v === NONE ? undefined : v })}>
            <SelectTrigger className="h-9" aria-label="font file"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>No bundled file</SelectItem>
              {fontAssets.map((a) => (
                <SelectItem key={a.id} value={a.id}>{fileLabel(a)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Labeled>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <NumField label="Size" value={value.size} min={1} onChange={(size) => onChange({ ...value, size })} />
        <NumField label="Weight" value={value.weight} min={100} max={900} step={100} onChange={(weight) => onChange({ ...value, weight })} />
        <NumField label="Line height" value={value.line_height} min={0.1} max={4} step={0.05} onChange={(line_height) => onChange({ ...value, line_height })} />
        <NumField label="Max scale" value={value.max_scale} min={1} max={4} step={0.1} onChange={(max_scale) => onChange({ ...value, max_scale })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        {/* POINTS, and added between adjacent characters only (SPEC §6.4b). Bounded both ways: past
            −2pt glyphs collide at body sizes, past +10pt a heading fits no phone — and both ends are
            near enough to catch a mistyped unit, someone entering 16 meaning "16% of an em". */}
        <NumField label="Tracking" value={value.letter_spacing ?? 0} min={-2} max={10} step={0.1} onChange={(letter_spacing) => onChange({ ...value, letter_spacing: letter_spacing || undefined })} />
        <Labeled label="Style">
          {/* A TOKEN decision, like weight — not a per-node toggle. It needs a real italic face in
              the font file above; synthetic oblique is forbidden (SPEC §4.1), because a skewed
              upright has different advances on every platform that skews it. */}
          <Select value={value.style ?? 'normal'} onValueChange={(v) => onChange({ ...value, style: v === 'italic' ? 'italic' : undefined })}>
            <SelectTrigger className="h-9" aria-label="font style"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="italic">Italic</SelectItem>
            </SelectContent>
          </Select>
        </Labeled>
      </div>
      {fontAssets.length === 0 ? <p className="text-[11px] text-muted-foreground">No font files uploaded — add one on the Assets page, then attach it here.</p> : null}
    </div>
  );
}

function RadiusEditor({ value, onChange }: { value: number | Corners; onChange: (value: number | Corners) => void }): React.JSX.Element {
  const uniform = typeof value === 'number';
  const corners = uniform ? { tl: value, tr: value, br: value, bl: value } : value;
  const cap = (v: number) => Math.min(v, 28);
  const preview = uniform ? cap(value) : `${cap(corners.tl)}px ${cap(corners.tr)}px ${cap(corners.br)}px ${cap(corners.bl)}px`;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Select value={uniform ? 'uniform' : 'corners'} onValueChange={(mode) => onChange(mode === 'uniform' ? corners.tl : { ...corners })}>
          <SelectTrigger className="h-9 w-40" aria-label="radius shape"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="uniform">Uniform</SelectItem>
            <SelectItem value="corners">Per-corner</SelectItem>
          </SelectContent>
        </Select>
        <span aria-hidden className="size-10 shrink-0 border bg-muted" style={{ borderRadius: preview }} />
      </div>
      {uniform ? (
        <NumField label="Radius" value={value} min={0} onChange={(next) => onChange(next)} />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(['tl', 'tr', 'bl', 'br'] as const).map((c) => (
            <NumField key={c} label={c.toUpperCase()} value={corners[c]} min={0} onChange={(next) => onChange({ ...corners, [c]: next })} />
          ))}
        </div>
      )}
    </div>
  );
}

function ElevationEditor({ value, theme, onChange }: { value: Shadow; theme: Theme; onChange: (value: Shadow) => void }): React.JSX.Element {
  const c = theme.colors[value.color]?.light;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span aria-hidden className="h-12 flex-1 rounded-md border bg-background" style={{ boxShadow: `${value.x ?? 0}px ${value.y}px ${value.blur}px ${value.spread ?? 0}px ${c ?? 'rgba(0,0,0,0.3)'}` }} />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <NumField label="X" value={value.x ?? 0} min={-100} onChange={(x) => onChange({ ...value, x })} />
        <NumField label="Y" value={value.y} min={-100} onChange={(y) => onChange({ ...value, y })} />
        <NumField label="Blur" value={value.blur} min={0} onChange={(blur) => onChange({ ...value, blur })} />
        <NumField label="Spread" value={value.spread ?? 0} min={0} onChange={(spread) => onChange({ ...value, spread })} />
      </div>
      <Labeled label="Colour">
        <ColorTokenSelect label="elevation colour" value={value.color} theme={theme} onChange={(color) => color && onChange({ ...value, color })} />
      </Labeled>
    </div>
  );
}

function GradientEditor({ value, theme, onChange }: { value: Gradient; theme: Theme; onChange: (value: Gradient) => void }): React.JSX.Element {
  const strip = `linear-gradient(${value.angle}deg, ${value.stops.map((s) => theme.colors[s]?.light ?? 'transparent').join(', ')})`;
  return (
    <div className="space-y-3">
      <span aria-hidden className="block h-10 rounded-md border" style={{ backgroundImage: strip }} />
      <div className="grid grid-cols-2 gap-3">
        <NumField label="Angle" value={value.angle} min={0} max={360} onChange={(angle) => onChange({ ...value, angle })} />
      </div>
      <div className="space-y-2">
        {value.stops.map((stop, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="flex-1">
              <ColorTokenSelect label={`stop ${i + 1}`} value={stop} theme={theme} onChange={(c) => c && onChange({ ...value, stops: value.stops.map((s, j) => (j === i ? c : s)) })} />
            </span>
            {value.stops.length > 2 ? (
              <Button variant="ghost" size="icon" className="size-8 text-muted-foreground hover:text-destructive" aria-label={`Remove stop ${i + 1}`} onClick={() => onChange({ ...value, stops: value.stops.filter((_, j) => j !== i) })}>
                <X className="size-3.5" />
              </Button>
            ) : null}
          </div>
        ))}
        {value.stops.length < 8 ? (
          <Button variant="outline" size="sm" onClick={() => onChange({ ...value, stops: [...value.stops, value.stops[value.stops.length - 1]!] })}>
            <Plus className="size-3.5" /> Add stop
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function ButtonEditor({ value, theme, onChange }: { value: ButtonVariant; theme: Theme; onChange: (value: ButtonVariant) => void }): React.JSX.Element {
  const radii = Object.keys(theme.radii);
  const gradients = Object.keys(theme.gradients);
  const elevations = Object.keys(theme.elevations);
  const bgValue = value.bg === null ? NONE : typeof value.bg === 'string' ? `c:${value.bg}` : `g:${value.bg.gradient}`;
  const radiusIsToken = typeof value.radius === 'string';
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Labeled label="Background">
          <Select
            value={bgValue}
            onValueChange={(v) => onChange({ ...value, bg: v === NONE ? null : v.startsWith('g:') ? { gradient: v.slice(2) } : v.slice(2) })}
          >
            <SelectTrigger className="h-9" aria-label="button background"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>None (text only)</SelectItem>
              <SelectGroup>
                <SelectLabel>Colours</SelectLabel>
                {Object.keys(theme.colors).map((c) => <SelectItem key={c} value={`c:${c}`}>{c}</SelectItem>)}
              </SelectGroup>
              {gradients.length > 0 ? (
                <SelectGroup>
                  <SelectLabel>Gradients</SelectLabel>
                  {gradients.map((g) => <SelectItem key={g} value={`g:${g}`}>{g}</SelectItem>)}
                </SelectGroup>
              ) : null}
            </SelectContent>
          </Select>
        </Labeled>
        <Labeled label="Foreground">
          <ColorTokenSelect label="button foreground" value={value.fg} theme={theme} onChange={(fg) => fg && onChange({ ...value, fg })} />
        </Labeled>
        <Labeled label="Radius">
          <Select
            value={radiusIsToken ? (value.radius as string) : CUSTOM}
            onValueChange={(v) => onChange({ ...value, radius: v === CUSTOM ? (typeof value.radius === 'number' ? value.radius : 8) : v })}
          >
            <SelectTrigger className="h-9" aria-label="button radius"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={CUSTOM}>Custom value</SelectItem>
              {radii.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </Labeled>
        <Labeled label="Elevation">
          <Select value={value.elevation ?? NONE} onValueChange={(v) => onChange({ ...value, elevation: v === NONE ? undefined : v })}>
            <SelectTrigger className="h-9" aria-label="button elevation"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>None</SelectItem>
              {elevations.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
        </Labeled>
      </div>
      {!radiusIsToken ? (
        <NumField label="Radius value" value={typeof value.radius === 'number' ? value.radius : 0} min={0} onChange={(radius) => onChange({ ...value, radius })} />
      ) : null}
      <Labeled label="Padding">
        <div className="grid grid-cols-4 gap-2">
          {(['top', 'end', 'bottom', 'start'] as const).map((side) => (
            <NumField key={side} label={side} value={value.padding[side]} min={0} onChange={(next) => onChange({ ...value, padding: { ...value.padding, [side]: next } })} />
          ))}
        </div>
      </Labeled>
    </div>
  );
}

function fileLabel(asset: Asset): string {
  return (asset.r2_key.split('/').pop() ?? asset.r2_key).replace(/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}-/i, '');
}
